import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaLeaf, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import PageTemplate from '../../components/Common/PageTemplate';

const BeytepGoleteleriAcigolKorumaProjesi = () => {
  // Fotoğraf URL'leri
  const images = [
    'https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/4.jfif',
    'https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/download.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/5.jfif',
    'https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/6.jfif',
    'https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/7.jfif',
    'https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/8.jfif',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/acigoldislisazancigi1.webp',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/acigoldislisazancigi2.webp',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/acigoldislisazancigi3.webp',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/acigoldislisazancigi4.webp',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/acigoldislisazancigi5.webp',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/acigoldislisazancigi6.webp',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/acigoldislisazancigi7.webp',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/acigoldislisazancigi8.webp',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/2024_Mayis_AA-34643032.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/2024_Mayis_AA-34643044.jpg',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/thumbs_b_c_b7913ddc062c9aa4da225372e7097671.jpg'
  ];

  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState({});

  // Modal açma fonksiyonu
  const openModal = useCallback((index) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  // Modal kapatma fonksiyonu
  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  // Sonraki fotoğraf
  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  // Önceki fotoğraf
  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Klavye kontrolü
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!modalOpen) return;

      switch(e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [modalOpen, closeModal, nextImage, prevImage]);

  // Fotoğraf yüklenme durumu
  const handleImageLoad = useCallback((index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  }, []);

  // Preload next and previous images
  useEffect(() => {
    if (modalOpen && images.length > 0) {
      const preloadIndexes = [
        (currentImageIndex + 1) % images.length,
        (currentImageIndex - 1 + images.length) % images.length
      ];

      preloadIndexes.forEach(index => {
        const img = new Image();
        img.src = images[index];
      });
    }
  }, [currentImageIndex, modalOpen, images]);

  // Modal component
  const ImageModal = () => {
    if (!modalOpen || images.length === 0) return null;

    return (
      <div
        className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
        onClick={closeModal}
      >
        {/* Close button */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 z-60 text-white hover:text-gray-300 transition-colors"
        >
          <FaTimes size={24} />
        </button>

        {/* Image counter */}
        <div className="absolute top-4 left-4 z-60 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full text-sm">
          {currentImageIndex + 1} / {images.length}
        </div>

        {/* Previous button */}
        {images.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 z-60 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
          >
            <FaChevronLeft size={20} />
          </button>
        )}

        {/* Next button */}
        {images.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 z-60 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
          >
            <FaChevronRight size={20} />
          </button>
        )}

        {/* Main image */}
        <div
          className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={images[currentImageIndex]}
            alt={`Acıgöl Dişlisazancığı Projesi ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            loading="eager"
          />
        </div>

        {/* Image title */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-60 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full text-sm">
          Acıgöl Dişlisazancığı Projesi - Fotoğraf {currentImageIndex + 1}
        </div>
      </div>
    );
  };

  return (
    <PageTemplate
      title='Dünyanın İlk "Yeşil Liste" Statülü Tatlısu Balığı Kampüsümüzde Koruma Altında'
      subtitle="Acıgöl dişlisazancığı (Anatolichthys transgrediens) Hacettepe Üniversitesi Beytepe Göletlerinde güvenli bir yaşam alanı buldu."
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Ana İçerik */}
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl -mt-16 relative z-10 mb-16">
          {/* Proje Bilgileri */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <FaCalendarAlt className="text-blue-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">Tarih</p>
                <p className="text-gray-800 font-semibold">Mayıs 2024</p>
                <p className="text-gray-600 text-sm">Proje Başlangıcı</p>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-100">
              <FaMapMarkerAlt className="text-green-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-600 uppercase tracking-wide">Yer</p>
                <p className="text-gray-800 font-semibold">Beytepe Göletleri</p>
                <p className="text-gray-600 text-sm">Hacettepe Üniversitesi</p>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start space-x-3 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl border border-purple-100">
              <FaLeaf className="text-purple-600 text-xl flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">Durum</p>
                <p className="text-gray-800 font-semibold">IUCN Yeşil Statü</p>
                <p className="text-gray-600 text-sm">Dünya İlki</p>
              </div>
            </div>
          </div>

          {/* Ana Metin */}
          <div className="prose prose-lg max-w-none space-y-6">
            <div className="bg-gradient-to-r from-hacettepe-blue/5 to-hacettepe-red/5 p-8 rounded-2xl border-l-4 border-hacettepe-blue">
              <p className="text-xl leading-relaxed text-gray-700 mb-6">
                Dünyanın en çok tehdit altında olduğu kabul edilen 100 türünden biri olan <span className="font-semibold text-hacettepe-blue">Acıgöl dişlisazancığı (Anatolichthys transgrediens)</span> dünya üzerinde sadece Acıgöl kapalı havzasındaki (Denizli/Afyonkarahisar) çok dar bir kaynak alanında yaşamaktadır.
              </p>

              <p className="text-lg leading-relaxed text-gray-700">
                <span className="font-semibold text-hacettepe-red">IUCN (Dünya Doğayı Koruma Birliği) Kırmızı Listesi'nde &quot;Kritik Düzeyde Tehlike Altında (CR)&quot;</span> olarak sınıflandırılan bu tür, iklim değişikliğine bağlı kuraklık, su çekilmesi ve istilacı türlerin baskısı nedeniyle yok olmanın eşiğindedir.
              </p>
            </div>

            {/* Koruma Girişimi Bölümü */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="w-2 h-8 bg-hacettepe-blue rounded-full mr-3"></span>
                Hacettepe Üniversitesi'nin Öncü Rolü
              </h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                Türün korunmasına yönelik en önemli adımlardan biri <span className="font-semibold text-hacettepe-blue">Hacettepe Üniversitesi Fen Fakültesi</span> liderliğinde Beytepe Yerleşkesi'nde atıldı. Tarım ve Orman Bakanlığı ve Hacettepe Üniversitesi Biyoloji Bölümünden Prof. Dr. F. Güler EKMEKÇİ ve Doç. Dr. Baran YOĞURTÇUOĞLU tarafından yürütülen bu girişimde <span className="font-semibold">Mayıs 2024</span> tarihinde Bakanlık bünyesinde üretilen <span className="font-semibold text-green-700">200 birey</span> üniversitemiz Beytepe Göletlerine başarılı bir şekilde bırakılmıştır.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                Bu koruma faaliyetinin amacı, Acıgöl'deki doğal yaşam alanı tamamen kurusa veya bozulsa bile, türün varlığını devam ettirebileceği güvenli bir <span className="font-semibold text-purple-700">&quot;Nuh'un Gemisi&quot; popülasyonu (safety net population)</span> oluşturmaktır.
              </p>
            </div>

            {/* İzleme Çalışmaları Bölümü */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-2xl border-2 border-green-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="w-2 h-8 bg-green-600 rounded-full mr-3"></span>
                İzleme Çalışmaları ve Mevcut Durum
              </h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                Mayıs 2024'ten bu yana yürütülen düzenli izleme çalışmaları, hem umut verici gelişmeleri hem de çözüm bekleyen bazı zorlukları ortaya koymuştur. Acıgöl'ün doğal ortamına kıyasla Ankara'nın daha sert geçen kış koşullarına rağmen, Hacettepe'ye taşınan bireylerin <span className="font-semibold text-green-700">en azından bir kısmının kışı başarıyla atlattığı ve hayatta kaldığı</span> tespit edilmiştir.
              </p>
              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                Bu durum, türün adaptasyon yeteneğinin ve göletlerimizin uygunluğunun bir göstergesi olmuştur. Ancak yine de henüz hedeflenen popülasyon yoğunluğuna ulaşılamamıştır. Bunun sebebi olarak kurucu popülasyon birey sayısının düşük olması (200 birey) olarak değerlendirilmiştir.
              </p>
            </div>

            {/* Dünyada Bir İlk Bölümü */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl border-2 border-yellow-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="w-2 h-8 bg-yellow-600 rounded-full mr-3"></span>
                Dünyada Bir İlk: &quot;Green Status&quot; ve Hacettepe Vizyonu
              </h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                Hacettepe Üniversitesi olarak ortağı olduğumuz bu proje kapsamında, <span className="font-semibold text-yellow-700">Acıgöl Dişli Sazancığı, dünyada IUCN Green Status (Yeşil Statü) değerlendirmesine alınan ilk tatlısu balığı olma unvanına erişmiştir</span>. Bu statü, türün koruma çalışmaları sayesinde toparlanma (recovery) potansiyelini ölçen yeni bir standardı olarak IUCN tarafından geliştirilmiştir.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                <span className="font-semibold text-hacettepe-blue">Hacettepe Beytepe Göletleri</span>, bu türün gelecekteki &quot;tam iyileşme&quot; senaryosunun merkezinde yer almaktadır.
              </p>
            </div>

            {/* Gelecek Projeksiyonu Bölümü */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl border-2 border-purple-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="w-2 h-8 bg-purple-600 rounded-full mr-3"></span>
                Gelecek Projeksiyonu ve Sürdürülebilirlik
              </h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                Henüz yolun başındayız; ancak hedeflerimiz büyük. İlk etapta bırakılan 200 bireye ek olarak, doğal ortamı dışında (ex-situ) üretilen bireyler kullanılarak <span className="font-semibold text-purple-700">popülasyon takviyesi yapılması planlanmaktadır</span>. Bunun yanı sıra, türün Beytepe göletlerinde hayatta kalma oranını artıracak habitat düzenlemesi seçenekleri de (sığınak alanlarının oluşturulması vb.) gündemdedir.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                Popülasyon sayısının istenen düzeylere gelmesi ve popülasyonun kendi kendine yetebilen bir düzeye ulaşmasıyla birlikte, bu tür özelinde ancak daha geniş çıkarımları yansıtacak şekilde farkındalık ve eğitim çalışmaları yapılması hedeflenmektedir. <span className="font-semibold text-hacettepe-blue">Hacettepe'nin bu konudaki liderliği, biyoçeşitliliğin kampüslerde korunması adına, çağdaş üniversite yönetimleri için ulusal ve uluslararası düzeyde referans alınacak örnek bir model teşkil edecektir</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Fotoğraf Galerisi - İçerik gelince güncellenecek */}
        {images.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
                Projeden Kareler
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-hacettepe-blue to-hacettepe-red mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((src, index) => (
                <div
                  key={index}
                  className="group relative aspect-video overflow-hidden rounded-2xl shadow-lg bg-gray-200 cursor-pointer transform hover:scale-105 transition-all duration-300"
                  onClick={() => openModal(index)}
                >
                  {/* Loading placeholder */}
                  {!imageLoaded[index] && (
                    <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-hacettepe-blue border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}

                  <img
                    src={src}
                    alt={`Acıgöl Dişlisazancığı Projesi ${index + 1}`}
                    className={`w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110 ${
                      imageLoaded[index] ? 'opacity-100' : 'opacity-0'
                    }`}
                    loading="eager"
                    onLoad={() => handleImageLoad(index)}
                    onError={() => handleImageLoad(index)}
                  />

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                  {/* Hover content */}
                  <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0">
                    <p className="text-sm font-medium">Koruma Projesi</p>
                    <p className="text-xs opacity-75">Fotoğraf {index + 1} • Tıklayın</p>
                  </div>

                  {/* Click indicator */}
                  <div className="absolute top-4 right-4 bg-black bg-opacity-50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="w-4 h-4 border-2 border-white rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Geri Dön Butonu */}
        <div className="text-center">
          <Link
            to="/haberler"
            className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-gray-300"
          >
            <FaArrowLeft className="mr-3 -ml-1 h-5 w-5 group-hover:-translate-x-1 transition-transform duration-300" />
            Tüm Haberlere Geri Dön
          </Link>
        </div>

        {/* Image Modal */}
        <ImageModal />
      </div>
    </PageTemplate>
  );
};

export default BeytepGoleteleriAcigolKorumaProjesi;
